import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Edit2, Trash2, Loader2, Save, X, Search,
  Stethoscope, DollarSign, Clock, Image as ImageIcon
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Injectables',
  'Neurotoxins',
  'PDO Threads',
  'Lasers',
  'Microneedling',
  'Chemical Peels',
  'Hydrafacial',
  'Facials',
  'Body Treatments',
  'Wellness',
  'Beauty',
  'Other'
];

const PRICE_UNITS = [
  'per treatment',
  'per session',
  'per unit',
  'per syringe',
  'per vial',
  'per area',
  'starting price',
  'consultation'
];

const AdminTreatments = () => {
  const { toast } = useToast();
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Injectables',
    price: '',
    priceUnit: 'per treatment',
    duration: '',
    description: '',
    image: '',
    skinConditions: '',
    popular: false,
    active: true
  });

  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/treatments?practiceId=demo-practice-001`);
      const data = await response.json();
      setTreatments(data);
    } catch (err) {
      console.error('Error fetching treatments:', err);
      toast({
        title: 'Error',
        description: 'Failed to load treatments.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const treatmentData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        duration: parseInt(formData.duration) || 30,
        skinConditions: formData.skinConditions.split(',').map(s => s.trim()).filter(Boolean),
        practiceId: 'demo-practice-001'
      };

      let response;
      if (editingTreatment) {
        response = await fetch(`${API}/treatments/${editingTreatment.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(treatmentData)
        });
      } else {
        response = await fetch(`${API}/treatments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(treatmentData)
        });
      }

      if (!response.ok) throw new Error('Failed to save treatment');

      toast({
        title: editingTreatment ? 'Treatment Updated' : 'Treatment Added',
        description: `${formData.name} has been ${editingTreatment ? 'updated' : 'added'} successfully.`
      });

      resetForm();
      fetchTreatments();
    } catch (err) {
      console.error('Error saving treatment:', err);
      toast({
        title: 'Error',
        description: 'Failed to save treatment. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (treatment) => {
    setEditingTreatment(treatment);
    setFormData({
      name: treatment.name || '',
      category: treatment.category || 'Injectables',
      price: treatment.price?.toString() || '',
      priceUnit: treatment.priceUnit || 'per treatment',
      duration: treatment.duration?.toString() || '',
      description: treatment.description || '',
      image: treatment.image || '',
      skinConditions: (treatment.skinConditions || []).join(', '),
      popular: treatment.popular || false,
      active: treatment.active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (treatment) => {
    if (!window.confirm(`Are you sure you want to delete "${treatment.name}"?`)) return;

    try {
      const response = await fetch(`${API}/treatments/${treatment.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete treatment');

      toast({
        title: 'Treatment Deleted',
        description: `${treatment.name} has been deleted.`
      });

      fetchTreatments();
    } catch (err) {
      console.error('Error deleting treatment:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete treatment.',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Injectables',
      price: '',
      priceUnit: 'per treatment',
      duration: '',
      description: '',
      image: '',
      skinConditions: '',
      popular: false,
      active: true
    });
    setEditingTreatment(null);
    setShowForm(false);
  };

  const filteredTreatments = treatments.filter(t => {
    const matchesSearch = t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         t.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedTreatments = filteredTreatments.reduce((acc, treatment) => {
    const category = treatment.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(treatment);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading treatments...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Treatment Menu</h1>
          <p className="text-gray-600 mt-2">Manage your spa's treatment offerings and pricing</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Treatment
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Treatments</p>
                <p className="text-2xl font-bold">{treatments.length}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Categories</p>
                <p className="text-2xl font-bold">{Object.keys(groupedTreatments).length}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg. Price</p>
                <p className="text-2xl font-bold">
                  ${treatments.length > 0 ? Math.round(treatments.reduce((sum, t) => sum + (t.price || 0), 0) / treatments.length) : 0}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Popular Items</p>
                <p className="text-2xl font-bold">{treatments.filter(t => t.popular).length}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search treatments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border rounded-lg bg-white"
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Treatment Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingTreatment ? 'Edit Treatment' : 'Add New Treatment'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Fill in the treatment details below
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Treatment Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Lip Filler"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                      required
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="price">Price *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="price"
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        placeholder="0"
                        required
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="priceUnit">Price Unit</Label>
                    <select
                      id="priceUnit"
                      value={formData.priceUnit}
                      onChange={(e) => setFormData({...formData, priceUnit: e.target.value})}
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                    >
                      {PRICE_UNITS.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="duration">Duration (mins)</Label>
                    <div className="relative mt-1">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="duration"
                        type="number"
                        value={formData.duration}
                        onChange={(e) => setFormData({...formData, duration: e.target.value})}
                        placeholder="30"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe the treatment and its benefits..."
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="image">Image URL</Label>
                  <div className="relative mt-1">
                    <ImageIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="image"
                      value={formData.image}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      placeholder="https://example.com/image.jpg"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="skinConditions">Treats (comma-separated)</Label>
                  <Input
                    id="skinConditions"
                    value={formData.skinConditions}
                    onChange={(e) => setFormData({...formData, skinConditions: e.target.value})}
                    placeholder="e.g., wrinkles, fine-lines, volume-loss"
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.popular}
                      onChange={(e) => setFormData({...formData, popular: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Mark as Popular</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Active (visible to patients)</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {editingTreatment ? 'Update Treatment' : 'Add Treatment'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Treatments List */}
      {Object.keys(groupedTreatments).length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Stethoscope className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No treatments found</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first treatment</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Treatment
            </Button>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedTreatments).map(([category, categoryTreatments]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {category}
                <Badge variant="secondary">{categoryTreatments.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {categoryTreatments.map((treatment) => (
                  <div key={treatment.id} className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {treatment.image ? (
                        <img 
                          src={treatment.image} 
                          alt={treatment.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Stethoscope className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{treatment.name}</h4>
                          {treatment.popular && (
                            <Badge className="bg-primary text-xs">Popular</Badge>
                          )}
                          {!treatment.active && (
                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-1">{treatment.description}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span className="font-medium text-gray-900">
                            ${treatment.price} {treatment.priceUnit}
                          </span>
                          {treatment.duration && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {treatment.duration} min
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(treatment)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(treatment)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default AdminTreatments;
